function getAjaxBase() {
    const segments = window.location.pathname.split('/').filter(Boolean);
    return segments[0] === 'en' ? '/en' : '';
}

export default class ShoppingLayer {
    constructor() {
        this.overlay = document.querySelector('.shopping-overlay');
        this.layer = document.querySelector('.shopping-layer');
        if (!this.overlay || !this.layer) return;

        this.cartIcon = document.querySelector('.site-header-tool-cart');
        this.isAnimating = false;
        this.isOpen = false;
        this.init();
    }

    init() {
        this.bindAddToCart();
        this.bindClose();
        this.bindRemoveItem();
    }

    bindAddToCart() {
        document.addEventListener('submit', (e) => {
            const form = e.target.closest('form.cart, .product-card-add-to-cart');
            if (!form) return;
            e.preventDefault();
            this.handleAddToCart(form);
        });
    }

    getFormCartData(form) {
        const productIdInput = form.querySelector('[name="add-to-cart"]');
        const productId = productIdInput ? productIdInput.value : '';

        const wrapper = form.closest('.product-hero, .product-card');
        const visibleInput = wrapper ? wrapper.querySelector('.product-quantity-input') : null;
        const quantity = visibleInput ? parseInt(visibleInput.value, 10) || 1 : 1;

        const body = new URLSearchParams();
        body.append('add-to-cart', productId);
        body.append('quantity', quantity);
        return body;
    }

    async handleAddToCart(form) {
        const body = this.getFormCartData(form);
        const btn = form.querySelector('.single_add_to_cart_button, .cbd-btn-primary--cart');

        const ajaxPromise = this.ajaxAddToCart(body);
        const animPromise = btn && this.cartIcon ? this.flyAnimation(btn) : Promise.resolve();

        const data = await ajaxPromise;

        if (data && data.fragments) {
            this.applyFragments(data.fragments);
        }

        await this.refreshLayer();

        this.resetQuantity(form);

        await animPromise;

        const count = document.querySelector('.site-header-cart-count');
        if (count && parseInt(count.textContent, 10) > 0) {
            this.open();
        }
    }

    async ajaxAddToCart(body) {
        try {
            const response = await fetch(getAjaxBase() + '/?wc-ajax=add_to_cart', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
            });
            if (!response.ok) {
                throw new Error('HTTP ' + response.status);
            }
            return await response.json();
        } catch (err) {
            return null;
        }
    }

    async refreshLayer() {
        try {
            const response = await fetch(getAjaxBase() + '/?wc-ajax=cbdonna_refresh_layer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            });
            const data = await response.json();
            if (data && data.success) {
                const content = document.querySelector('.shopping-layer-content');
                if (content) content.innerHTML = data.data.html;

                const countEl = document.querySelector('.site-header-cart-count');
                if (countEl) {
                    countEl.outerHTML = '<span class="site-header-cart-count">' + data.data.cart_count + '</span>';
                }
            }
        } catch (err) {
            // silent fail
        }
    }

    resetQuantity(form) {
        const container = form.querySelector('.product-quantity');
        if (!container) return;

        const input = container.querySelector('.product-quantity-input');
        if (input) input.value = 1;

        const hidden = form.querySelector('.product-hero-qty-hidden, .product-card-qty-hidden');
        if (hidden) hidden.value = 1;
    }

    applyFragments(fragments) {
        for (const [selector, html] of Object.entries(fragments)) {
            if (selector === '.site-header-cart-count') {
                const el = document.querySelector(selector);
                if (el) el.outerHTML = html;
            } else if (selector === '.shopping-layer-content') {
                const el = document.querySelector(selector);
                if (el) el.innerHTML = html;
            }
        }
    }

    flyAnimation(btn) {
        if (this.isAnimating) return Promise.resolve();
        this.isAnimating = true;

        return new Promise((resolve) => {
            const startRect = btn.getBoundingClientRect();
            const endRect = this.cartIcon.getBoundingClientRect();

            const clone = btn.cloneNode(true);
            clone.classList.add('shopping-fly-clone');
            clone.style.cssText = `
                position: fixed;
                z-index: 10000;
                pointer-events: none;
                width: ${startRect.width}px;
                height: ${startRect.height}px;
                left: ${startRect.left}px;
                top: ${startRect.top}px;
                transition: all 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
            `;
            document.body.appendChild(clone);

            void clone.offsetHeight;

            const deltaX = endRect.left + endRect.width / 2 - startRect.left - startRect.width / 2;
            const deltaY = endRect.top + endRect.height / 2 - startRect.top - startRect.height / 2;

            clone.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(0.3)`;
            clone.style.opacity = '0';

            clone.addEventListener('transitionend', () => {
                clone.remove();
                this.isAnimating = false;
                resolve();
            }, { once: true });
        });
    }

    open() {
        if (this.isOpen) return;
        this.isOpen = true;
        document.body.classList.add('shopping-layer-open');
        this.overlay.classList.add('is-open');
        this.layer.classList.add('is-open');
    }

    close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        document.body.classList.remove('shopping-layer-open');
        this.overlay.classList.remove('is-open');
        this.layer.classList.remove('is-open');
    }

    bindClose() {
        const closeBtn = this.layer.querySelector('.shopping-layer-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.close());
        }

        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) {
                this.close();
            }
        });
    }

    bindRemoveItem() {
        document.addEventListener('click', (e) => {
            const removeBtn = e.target.closest('.shopping-layer-item-remove');
            if (!removeBtn) return;

            e.preventDefault();

            const item = removeBtn.closest('.shopping-layer-item');
            const key = item ? item.dataset.cartItemKey : null;
            if (!key) return;

            this.removeItem(key);
        });
    }

    async removeItem(cartItemKey) {
        const body = new URLSearchParams();
        body.append('cart_item_key', cartItemKey);

        try {
            const response = await fetch(getAjaxBase() + '/?wc-ajax=remove_from_cart', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
            });
            const data = await response.json();

            if (data && data.fragments) {
                this.applyFragments(data.fragments);
            }

            await this.refreshLayer();

            const count = document.querySelector('.site-header-cart-count');
            if (count && parseInt(count.textContent, 10) === 0) {
                this.close();
            }
        } catch (err) {
            // silent fail
        }
    }
}
